import { useState, useEffect, useMemo } from 'react';
import { mockDocuments, mockDocumentNumberRules } from '../../data/mockData.ts';
import { DocumentStatus } from '../../types/document.ts';
import { ArrowLeft, Upload, Save, Send, X, Plus, Trash2, UserCircle, ChevronDown, ChevronUp, Users, GitBranch } from 'lucide-react';
import { getAllUsers } from '../../services/userService.ts';
import { getAllTemplates, getStepsByTemplateId } from '../../services/workflowService.ts';
import { createDocument, updateDocument } from '../../services/documentService.ts';

interface DocumentEditorProps {
  documentId: string | null;
  onBack: () => void;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ApprovalStep {
  id: string;
  stepNumber: number;
  stepName: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface ApprovalTemplate {
  id: string;
  templateName: string;
  documentType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
}

export function DocumentManageCreate({ documentId, onBack }: DocumentEditorProps) {
  // Fetch approvers from userService API
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [templates, setTemplates] = useState<ApprovalTemplate[]>([]);

  useEffect(() => {
    const fetchApprovers = async () => {
      try {
        const users = await getAllUsers();
        // Transform users to Approver format
        const approvers: Approver[] = users.map((user, index) => ({
          id: user.id?.toString() || `u${index + 1}`,
          name: user.userName || '',
          role: user.roleName || 'User',
          email: user.email || '',
        }));
        setApproversPool(approvers);
      } catch (error) {
        console.error('Failed to load approvers:', error);
        // Fallback to empty array if API fails
        setApproversPool([]);
      }
    };

    fetchApprovers();
  }, []);

  // Load workflow templates from backend API
  useEffect(() => {
    const loadWorkflowTemplates = async () => {
      try {
        const allTemplates = await getAllTemplates();
        console.log('Raw templates from API:', allTemplates);
        // Transform backend template structure to ApprovalTemplate format
        const transformedTemplates: ApprovalTemplate[] = allTemplates.map((template: any) => ({
          id: template.id?.toString() || '',
          templateName: template.name || template.templateName || '',
          documentType: template.docType || template.documentType || '',
          description: template.description || '',
          isActive: template.isActive !== false,
          steps: (template.steps || []).map((step: any) => ({
            id: step.id?.toString() || '',
            stepNumber: step.stepNo || step.stepNumber || 0,
            stepName: step.name || step.stepName || '',
            stepType: step.stepType || 'single',
            approvers: (step.approverIds || step.approvers || []).map((approverId: any) => {
              // If approverIds is just an array of IDs, look up from approversPool
              if (typeof approverId === 'number' || typeof approverId === 'string') {
                const foundApprover = approversPool.find(a => a.id === approverId.toString());
                if (foundApprover) {
                  return foundApprover;
                }
                return {
                  id: approverId.toString(),
                  name: `User ${approverId}`,
                  role: 'User',
                  email: '',
                };
              }
              // Otherwise assume it's an approver object
              return {
                id: approverId.id?.toString() || '',
                name: approverId.name || approverId.userName || '',
                role: approverId.role || approverId.roleName || 'User',
                email: approverId.email || '',
              };
            }),
            approvalRule: step.approvalRule || 'all',
            timeLimit: step.timeLimit || 1,
            description: step.description || '',
            isOptional: step.isOptional === true,
          }))
        }));
        console.log('Transformed templates:', transformedTemplates);
        setTemplates(transformedTemplates);
      } catch (error) {
        console.error('Failed to load workflow templates:', error);
        setTemplates([]);
      }
    };

    loadWorkflowTemplates();
  }, [approversPool]);

  // Use backend templates as primary source, fallback to mock if needed
  const mockTemplates: ApprovalTemplate[] = useMemo(() => {
    if (templates.length > 0) return templates;
    if (approversPool.length === 0) return [];
    
    return [
      {
        id: 't1',
        templateName: 'SOP Standard Approval Process',
        documentType: 'SOP',
        description: 'Complete approval process for standard operating procedures',
        isActive: true,
        steps: [
          {
            id: 's1',
            stepNumber: 1,
            stepName: 'Department Manager Review',
            stepType: 'single',
            approvers: [approversPool[0]],
            approvalRule: 'all',
            timeLimit: 2,
            description: 'Department manager conducts initial review of document content',
            isOptional: false
          },
          {
            id: 's2',
            stepNumber: 2,
            stepName: 'Technical Review',
            stepType: 'parallel',
            approvers: [approversPool[1], approversPool[7]].filter(a => a !== undefined),
            approvalRule: 'all',
            timeLimit: 3,
            description: 'Technical team reviews technical details in parallel',
            isOptional: false
          },
          {
            id: 's3',
            stepNumber: 3,
            stepName: 'Quality Manager Approval',
            stepType: 'single',
            approvers: [approversPool[2]],
            approvalRule: 'all',
            timeLimit: 2,
            description: 'Quality manager final approval',
            isOptional: false
          }
        ]
      },
      {
        id: 't2',
        templateName: 'Quick Approval Process',
        documentType: 'WI',
        description: 'Simplified quick approval process for work instructions',
        isActive: true,
        steps: [
          {
            id: 's1',
            stepNumber: 1,
            stepName: 'QC Manager Review',
            stepType: 'single',
            approvers: [approversPool[3]],
            approvalRule: 'all',
            timeLimit: 1,
            description: 'QC manager quick review',
            isOptional: false
          },
          {
            id: 's2',
            stepNumber: 2,
            stepName: 'Quality Manager Approval',
            stepType: 'single',
            approvers: [approversPool[2]],
            approvalRule: 'all',
            timeLimit: 1,
            description: 'Quality manager final approval',
            isOptional: false
          }
        ]
      },
      {
        id: 't3',
        templateName: 'Form Approval Process',
        documentType: 'FORM',
        description: 'Standard approval process for quality forms',
        isActive: true,
        steps: [
          {
            id: 's1',
            stepNumber: 1,
            stepName: 'Quality Engineer Review',
            stepType: 'single',
            approvers: [approversPool[4]],
            approvalRule: 'all',
            timeLimit: 1,
            description: 'Quality engineer reviews form design',
            isOptional: false
          },
          {
            id: 's2',
            stepNumber: 2,
            stepName: 'Quality Manager Approval',
            stepType: 'single',
            approvers: [approversPool[2]],
            approvalRule: 'all',
            timeLimit: 1,
            description: 'Quality manager approves publication',
            isOptional: false
          }
        ]
      },
      {
        id: 't4',
        templateName: 'Policy Document Approval Process',
        documentType: 'POLICY',
        description: 'Complete approval process for company policy documents',
        isActive: true,
        steps: [
          {
            id: 's1',
            stepNumber: 1,
            stepName: 'Compliance Review',
            stepType: 'single',
            approvers: [approversPool[4]],
            approvalRule: 'all',
            timeLimit: 3,
            description: 'Compliance specialist reviews policy compliance',
            isOptional: false
          },
          {
            id: 's2',
            stepNumber: 2,
            stepName: 'Quality Manager Review',
            stepType: 'single',
            approvers: [approversPool[2]],
            approvalRule: 'all',
            timeLimit: 2,
            description: 'Quality manager reviews quality-related content',
            isOptional: false
          },
          {
            id: 's3',
            stepNumber: 3,
            stepName: 'Director Approval',
            stepType: 'single',
            approvers: [approversPool[5]],
            approvalRule: 'all',
            timeLimit: 2,
            description: 'Director approval',
            isOptional: false
          }
        ]
      }
    ];
  }, [approversPool, templates]);

  const existingDoc = documentId ? mockDocuments.find(doc => doc.id === documentId) : null;
  
  const [formData, setFormData] = useState({
    title: existingDoc?.title || '',
    description: existingDoc?.description || '',
    category: existingDoc?.category || 'SOP',
    tags: existingDoc?.tags.join(', ') || '',
    versionChanges: '',
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [status, setStatus] = useState<DocumentStatus>(existingDoc?.status || 'Draft');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [approvalSteps, setApprovalSteps] = useState<ApprovalStep[]>([]);
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [showApprovalSection, setShowApprovalSection] = useState(true);
  const [saving, setSaving] = useState(false);

  const categories = mockDocumentNumberRules.map(rule => rule.category);

  // Get available templates for selected category
  const availableTemplates = mockTemplates.filter(
    t => t.documentType === formData.category && t.isActive
  );

  // Load template when selected
  useEffect(() => {
    if (selectedTemplateId) {
      const loadTemplateSteps = async () => {
        try {
          // First try to get steps from backend API
          const steps = await getStepsByTemplateId(selectedTemplateId);
          // Transform backend step structure to ApprovalStep format
          const transformedSteps: ApprovalStep[] = (steps || []).map((step: any) => ({
            id: step.id?.toString() || '',
            stepNumber: step.stepNo || step.stepNumber || 0,
            stepName: step.name || step.stepName || '',
            stepType: step.stepType || 'single',
            approvers: (step.approverIds || step.approvers || []).map((approverId: any) => {
              // If approverIds is just an array of IDs, look up from approversPool
              if (typeof approverId === 'number' || typeof approverId === 'string') {
                const foundApprover = approversPool.find(a => a.id === approverId.toString());
                if (foundApprover) {
                  return foundApprover;
                }
                return {
                  id: approverId.toString(),
                  name: `User ${approverId}`,
                  role: 'User',
                  email: '',
                };
              }
              // Otherwise assume it's an approver object
              return {
                id: approverId.id?.toString() || '',
                name: approverId.name || approverId.userName || '',
                role: approverId.role || approverId.roleName || 'User',
                email: approverId.email || '',
              };
            }),
            approvalRule: step.approvalRule || 'all',
            timeLimit: step.timeLimit || 1,
            description: step.description || '',
            isOptional: step.isOptional === true,
          }));
          setApprovalSteps(transformedSteps);
        } catch (error) {
          console.error('Failed to load template steps:', error);
          // Fallback to steps from mockTemplates
          const template = mockTemplates.find(t => t.id === selectedTemplateId);
          if (template) {
            setApprovalSteps(JSON.parse(JSON.stringify(template.steps)));
          } else {
            setApprovalSteps([]);
          }
        }
      };
      loadTemplateSteps();
    } else {
      setApprovalSteps([]);
    }
  }, [selectedTemplateId, mockTemplates, approversPool]);

  // Reset template selection when category changes
  useEffect(() => {
    setSelectedTemplateId('');
    setApprovalSteps([]);
  }, [formData.category]);

  const generateDocumentNumber = (category: string) => {
    const rule = mockDocumentNumberRules.find(r => r.category === category);
    if (rule) {
      return rule.example;
    }
    return 'QMS-DOC-2024-001';
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  // Add approver to step
  const handleAddApprover = (stepId: string, approver: Approver) => {
    setApprovalSteps(steps =>
      steps.map(s =>
        s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
      )
    );
  };

  // Remove approver from step
  const handleRemoveApprover = (stepId: string, approverId: string) => {
    setApprovalSteps(steps =>
      steps.map(s =>
        s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
      )
    );
  };

  // Add custom step
  const handleAddCustomStep = () => {
    const newStep: ApprovalStep = {
      id: `custom-${Date.now()}`,
      stepNumber: approvalSteps.length + 1,
      stepName: `Custom Step ${approvalSteps.length + 1}`,
      stepType: 'single',
      approvers: [],
      approvalRule: 'all',
      timeLimit: 2,
      description: '',
      isOptional: false
    };
    setApprovalSteps([...approvalSteps, newStep]);
  };

  // Remove step
  const handleRemoveStep = (stepId: string) => {
    if (true) {
      const updatedSteps = approvalSteps
        .filter(s => s.id !== stepId)
        .map((s, idx) => ({ ...s, stepNumber: idx + 1 }));
      setApprovalSteps(updatedSteps);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate approval workflow if submitting for review
    if (status === 'Review' && approvalSteps.length === 0) {
      alert('Please configure the approval workflow before submitting for review');
      return;
    }

    if (status === 'Review') {
      const stepsWithoutApprovers = approvalSteps.filter(s => s.approvers.length === 0 && !s.isOptional);
      if (stepsWithoutApprovers.length > 0) {
        alert(`The following required steps do not have approvers assigned:\n${stepsWithoutApprovers.map(s => `- ${s.stepName}`).join('\n')}`);
        return;
      }
    }

    setSaving(true);

    try {
      const documentData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        file: uploadedFile || undefined,
        status: status,
        approvalSteps: approvalSteps.length > 0 ? approvalSteps : undefined,
        documentNumber: existingDoc?.documentNumber || generateDocumentNumber(formData.category),
        currentVersion: existingDoc ? `${parseFloat(existingDoc.currentVersion) + 0.1}` : '1.0',
        createdBy: parseInt(localStorage.getItem('userId') || '1000'),
        templateId: selectedTemplateId || undefined,
      };

      let result;
      if (documentId) {
        result = await updateDocument(documentId, documentData);
      } else {
        result = await createDocument(documentData);
      }

      console.log('Document saved successfully:', result);
      
      let message = `Document ${documentId ? 'updated' : 'created'} successfully!\nNumber: ${result.documentNumber}\nStatus: ${result.status}`;
      if (status === 'Review' && approvalSteps.length > 0) {
        message += `\n\nApproval workflow created (${approvalSteps.length} steps)`;
        message += `\nFirst step approvers: ${approvalSteps[0].approvers.map(a => a.name).join(', ')}`;
      }
      
      alert(message);
    } catch (error: any) {
      console.error('Failed to save document:', error);
      alert(`Failed to save document: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAsDraft = () => {
    setStatus('Draft');
    setTimeout(() => {
      document.getElementById('submit-btn')?.click();
    }, 0);
  };

  const handleSubmitForReview = () => {
    setStatus('Review');
    setTimeout(() => {
      document.getElementById('submit-btn')?.click();
    }, 0);
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(2)} KB` : `${mb.toFixed(2)} MB`;
  };

  const getStepTypeBadge = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      single: {
        label: 'Single',
        color: 'bg-blue-100 text-blue-800',
        icon: <UserCircle className="size-3" />
      },
      parallel: {
        label: 'Parallel',
        color: 'bg-purple-100 text-purple-800',
        icon: <Users className="size-3" />
      },
      conditional: {
        label: 'Conditional',
        color: 'bg-orange-100 text-orange-800',
        icon: <GitBranch className="size-3" />
      }
    };
    const config = configs[type] || configs.single;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-6 full-width ">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <h2 className="text-gray-900">
          {documentId ? 'Edit Document' : 'Create New Document'}
        </h2>
        {!documentId && (
          <p className="text-sm text-gray-600 mt-1">
            Create a new document and configure the approval workflow
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Document Type *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Document number will be generated automatically: {generateDocumentNumber(formData.category)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter document description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter tags, separated by commas"
              />
            </div>

            {documentId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Version Change Notes
                </label>
                <textarea
                  value={formData.versionChanges}
                  onChange={(e) => setFormData({ ...formData, versionChanges: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe the main content of this revision"
                />
              </div>
            )}
          </div>
        </div>

        {/* File Upload */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document File</h3>
          
          {!uploadedFile ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 mb-2">Click to upload or drag files here</p>
              <p className="text-sm text-gray-500 mb-4">Supported formats: PDF, Word, Excel, etc. Max 50MB</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                Choose File
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                />
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                  <Upload className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{uploadedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(uploadedFile.size)}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>

        {/* Approval Workflow */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Approval Workflow Configuration</h3>
              <p className="text-sm text-gray-600 mt-1">Select an approval template or create a custom workflow</p>
            </div>
            <button
              type="button"
              onClick={() => setShowApprovalSection(!showApprovalSection)}
              className="text-gray-500 hover:text-gray-700"
            >
              {showApprovalSection ? <ChevronUp className="size-5" /> : <ChevronDown className="size-5" />}
            </button>
          </div>

          {showApprovalSection && (
            <div className="space-y-4">
              {/* Template Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Approval Template
                </label>
                <select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- No template (manual configuration) --</option>
                  {availableTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.templateName} ({template.steps.length} steps)
                    </option>
                  ))}
                </select>
                {selectedTemplateId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Template loaded. You can adjust approvers as needed.
                  </p>
                )}
              </div>

              {/* Approval Steps */}
              {approvalSteps.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">
                      Approval Steps ({approvalSteps.length})
                    </label>
                    <button
                      type="button"
                      onClick={handleAddCustomStep}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Plus className="size-4" />
                      Add Custom Step
                    </button>
                  </div>

                  <div className="space-y-3">
                    {approvalSteps.map((step, index) => (
                      <div key={step.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {step.stepNumber}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">{step.stepName}</h4>
                              {getStepTypeBadge(step.stepType)}
                              {step.isOptional && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                                  Optional
                                </span>
                              )}
                              {step.timeLimit && (
                                <span className="text-xs text-gray-500">
                                  {step.timeLimit} day limit
                                </span>
                              )}
                            </div>
                            {step.description && (
                              <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                            )}
                            
                            {/* Approvers */}
                            <div className="mb-2">
                              <div className="flex flex-wrap gap-2">
                                {step.approvers.map(approver => (
                                  <div
                                    key={approver.id}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm"
                                  >
                                    <UserCircle className="size-4 text-gray-600" />
                                    <div>
                                      <div className="font-medium text-gray-900">{approver.name}</div>
                                      <div className="text-xs text-gray-500">{approver.role}</div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveApprover(step.id, approver.id)}
                                      className="ml-1 text-gray-400 hover:text-red-600"
                                    >
                                      <X className="size-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCurrentStepId(step.id);
                                    setShowApproverModal(true);
                                  }}
                                  className="inline-flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-white hover:border-blue-500 hover:text-blue-600 transition-colors"
                                >
                                  <Plus className="size-4" />
                                  Add Approver
                                </button>
                              </div>
                              {!step.isOptional && step.approvers.length === 0 && (
                                <p className="text-xs text-red-600 mt-2">* At least one approver must be assigned to this required step</p>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveStep(step.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {approvalSteps.length === 0 && (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg bg-gray-50">
                  <GitBranch className="size-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No approval workflow configured yet</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Select an approval template or add custom steps
                  </p>
                  <button
                    type="button"
                    onClick={handleAddCustomStep}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="size-4" />
                    Add First Step
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSaveAsDraft}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="button"
            onClick={handleSubmitForReview}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {saving ? 'Submitting...' : 'Submit for Review'}
          </button>
          <button
            id="submit-btn"
            type="submit"
            className="hidden"
          >
            Submit
          </button>
        </div>
      </form>

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={() => {
                    setShowApproverModal(false);
                    setCurrentStepId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                {approversPool.map(approver => {
                  const currentStep = approvalSteps.find(s => s.id === currentStepId);
                  const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id);
                  
                  return (
                    <button
                      key={approver.id}
                      type="button"
                      onClick={() => {
                        if (!isAlreadyAdded) {
                          handleAddApprover(currentStepId, approver);
                        }
                      }}
                      disabled={isAlreadyAdded}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        isAlreadyAdded
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <UserCircle className="size-10 text-gray-400" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{approver.name}</div>
                          <div className="text-sm text-gray-600">{approver.role}</div>
                          <div className="text-xs text-gray-500">{approver.email}</div>
                        </div>
                        {isAlreadyAdded && (
                          <div className="text-green-600 text-xs">Added</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowApproverModal(false);
                  setCurrentStepId(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManageCreate;